/**
 * Time buckets for the unified /heute agenda.
 */

import { todayYmdZurich } from "./schoolAttention.js";

export const BUCKET_OVERDUE = "overdue";
export const BUCKET_TODAY = "today";
export const BUCKET_TOMORROW = "tomorrow";
export const BUCKET_THIS_WEEK = "thisWeek";
export const BUCKET_LATER = "later";

export const BUCKET_ORDER = [
  BUCKET_OVERDUE,
  BUCKET_TODAY,
  BUCKET_TOMORROW,
  BUCKET_THIS_WEEK,
  BUCKET_LATER,
];

export const BUCKET_LABELS = {
  [BUCKET_OVERDUE]: "Überfällig",
  [BUCKET_TODAY]: "Heute",
  [BUCKET_TOMORROW]: "Morgen",
  [BUCKET_THIS_WEEK]: "Diese Woche",
  [BUCKET_LATER]: "Später",
};

/** @param {string} ymd */
export function addDaysYmd(ymd, days) {
  const d = new Date(`${ymd}T12:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * End of calendar week (Sunday) for a YYYY-MM-DD date.
 * @param {string} ymd
 */
export function endOfWeekYmd(ymd) {
  const d = new Date(`${ymd}T12:00:00`);
  const dow = d.getDay();
  const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
  return addDaysYmd(ymd, daysUntilSunday);
}

/**
 * @param {object[]} items normalized heute schedule items
 * @param {string} [todayYmd]
 */
export function bucketHeuteScheduleItems(items, todayYmd = todayYmdZurich()) {
  const tomorrow = addDaysYmd(todayYmd, 1);
  const weekEnd = endOfWeekYmd(todayYmd);

  /** @type {Record<string, object[]>} */
  const buckets = {
    [BUCKET_OVERDUE]: [],
    [BUCKET_TODAY]: [],
    [BUCKET_TOMORROW]: [],
    [BUCKET_THIS_WEEK]: [],
    [BUCKET_LATER]: [],
  };

  for (const item of items) {
    const date = String(item.date || "");
    if (!date) {
      buckets[BUCKET_LATER].push(item);
      continue;
    }
    if (date < todayYmd) {
      buckets[BUCKET_OVERDUE].push(item);
    } else if (date === todayYmd) {
      buckets[BUCKET_TODAY].push(item);
    } else if (date === tomorrow) {
      buckets[BUCKET_TOMORROW].push(item);
    } else if (date <= weekEnd) {
      buckets[BUCKET_THIS_WEEK].push(item);
    } else {
      buckets[BUCKET_LATER].push(item);
    }
  }

  return buckets;
}

/**
 * @param {string} date YYYY-MM-DD
 * @param {string} [todayYmd]
 */
export function formatHeuteScheduleDate(date, todayYmd = todayYmdZurich()) {
  if (!date) return "—";
  const tomorrow = addDaysYmd(todayYmd, 1);
  if (date === todayYmd) return "Heute";
  if (date === tomorrow) return "Morgen";
  const d = new Date(`${date}T12:00:00`);
  const weekday = d.toLocaleDateString("de-CH", { weekday: "short" });
  const dayMonth = d.toLocaleDateString("de-CH", { day: "numeric", month: "short" });
  return `${weekday} ${dayMonth}`;
}

/**
 * @param {object} item
 * @param {string} [todayYmd]
 */
export function formatHeuteScheduleMeta(item, todayYmd = todayYmdZurich()) {
  const startLabel = formatHeuteScheduleDate(item.date, todayYmd);
  const end =
    item.end_date && item.end_date !== item.date
      ? formatHeuteScheduleDate(item.end_date, todayYmd)
      : "";
  const parts = [end ? `${startLabel} – ${end}` : startLabel];
  if (item.all_day === false && item.time) {
    parts.push(String(item.time).slice(0, 5));
  }
  if (item.child) parts.push(item.child);
  else if (item.place) parts.push(item.place);
  return parts.join(" · ");
}

export const ORIGIN_ICONS = {
  mail: "📧",
  capture: "📷",
  manual: "✏️",
};

export const ORIGIN_LABELS = {
  mail: "Eltern-Mail",
  capture: "Foto",
  manual: "Gemerkt",
};
